import { inject, injectable } from 'inversify';
import {
  type AuthClientInterface,
  type AuthenticatedUser,
  type GetAuthenticatedUserOptions,
} from './interfaces';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';

import {
  AuthError,
  isNotUndefinedOrNull,
  type Logger,
  TYPES,
} from '@bizzdesign/sdk-internal';
import { assertIsUUID } from '@bizzdesign/sdk-internal/uuid';

@injectable()
export class AuthorizationCodeAuthClient implements AuthClientInterface {
  constructor(@inject(TYPES.Logger) private readonly logger: Logger) {}

  async login(): Promise<void> {
    try {
      await signInWithRedirect();
    } catch (error) {
      this.logger.error({
        context: 'AuthClient',
        msg: 'Error while starting login flow',
        error: error,
      });
      throw new AuthError('Login flow failed', error);
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      this.logger.error({
        context: 'AuthClient',
        msg: 'Error while logging out',
        error: error,
      });
      throw new AuthError('Logout flow failed', error);
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      return isNotUndefinedOrNull(idToken);
    } catch (error) {
      if (error instanceof Error && error.message.includes('No current user')) {
        this.logger.debug({
          context: 'AuthClient',
          msg: 'No valid user session found',
          error: error,
        });
        return false;
      } else {
        this.logger.error({
          context: 'AuthClient',
          msg: 'Cannot validate user session',
          error: error,
        });
        throw new AuthError('Cannot validate user session', error);
      }
    }
  }

  async getAuthenticatedUser(
    options?: GetAuthenticatedUserOptions,
  ): Promise<AuthenticatedUser> {
    this.logger.debug({
      context: 'AuthClient',
      msg: 'Retrieving User Info from ID and Access tokens',
    });
    try {
      const session = await fetchAuthSession(
        options?.forceRefresh ? { forceRefresh: true } : undefined,
      );
      const idTokenPayload = session.tokens?.idToken?.payload;
      const accessPayload = session.tokens?.accessToken?.payload;
      if (
        isNotUndefinedOrNull(idTokenPayload) &&
        isNotUndefinedOrNull(accessPayload)
      ) {
        const userId = (accessPayload['user_id'] as string) ?? '';
        assertIsUUID(userId);
        return {
          userId: userId,
          firstName: (idTokenPayload['given_name'] as string) ?? '',
          lastName: (idTokenPayload['family_name'] as string) ?? '',
          email: (idTokenPayload['email'] as string) ?? '',
          roles: this.getRoles(accessPayload),
        };
      }
      throw new Error('ID token or Access token payload is undefined or null');
    } catch (error) {
      this.logger.error({
        context: 'AuthTokenProvider',
        msg: 'Error thrown while retrieving User Info',
        error: error,
      });
      throw new AuthError('User Info could not be retrieved', error);
    }
  }

  async ensureAuthenticated(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error')) {
      // Prevent infinite redirect loop when Cognito sends ?error=...

      const oauthError = params.get('error');
      const oauthErrorDescription = params.get('error_description');

      this.logger.error({
        context: 'AuthClient',
        msg: 'OAuth error received from Cognito stopping automatic login attempt',
        error: oauthError,
        payload: { error_description: oauthErrorDescription },
      });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if ((await this.isAuthenticated()) === true) {
      return;
    }
    await this.login();
  }

  private getRoles(tokenPayload: { [id: string]: unknown }): {
    [key: string]: boolean;
  } {
    return Object.fromEntries(
      Object.entries(tokenPayload)
        .filter(([key]) => key.startsWith('is_'))
        .map(([key, value]) => [
          key.replace('is_', ''),
          value === 'true' || value === true,
        ]),
    );
  }
}
